"use client";

import {
  Label,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ChartConfig, ChartContainer } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";

interface TotalData {
  total_lulus: number;
  total_isi_ukt: number;
  total_bayar_ukt: number;
}

interface PaymentProgressChartProps {
  data: { total: TotalData } | undefined;
  isLoading: boolean;
}

const chartConfig = {
  bayar: {
    label: "Sudah Bayar",
    color: "hsl(var(--chart-2))", // Hijau/Teal
  },
  isi: {
    label: "Isi UKT",
    color: "hsl(var(--chart-4))", // Kuning/Orange
  },
} satisfies ChartConfig;

export function PaymentProgressChart({
  data,
  isLoading,
}: PaymentProgressChartProps) {
  if (isLoading)
    return <Skeleton className="h-full w-full rounded-xl min-h-[350px]" />;

  const totalLulus = data?.total?.total_lulus || 0;
  const totalIsi = data?.total?.total_isi_ukt || 0;
  const totalBayar = data?.total?.total_bayar_ukt || 0;

  // Hitung persentase terhadap jumlah Lulus
  const chartData = [
    {
      name: "Isi UKT",
      value: totalIsi,
      fill: "var(--color-isi)",
      percentage:
        totalLulus > 0 ? Math.round((totalIsi / totalLulus) * 100) : 0,
    },
    {
      name: "Sudah Bayar",
      value: totalBayar,
      fill: "var(--color-bayar)",
      percentage:
        totalLulus > 0 ? Math.round((totalBayar / totalLulus) * 100) : 0,
    },
  ];

  return (
    <Card className="flex flex-col h-full">
      <CardHeader className="items-center pb-0 text-center">
        <CardTitle>Realisasi Pembayaran UKT</CardTitle>
        <CardDescription>
          Persentase dari total {totalLulus} peserta lulus.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pb-0">
        <ChartContainer
          config={chartConfig}
          className="mx-auto aspect-square max-h-[250px]"
        >
          <RadialBarChart
            data={chartData}
            startAngle={0}
            endAngle={250}
            innerRadius={80}
            outerRadius={110}
          >
            <PolarGrid
              gridType="circle"
              radialLines={false}
              stroke="none"
              className="first:fill-muted last:fill-background"
              polarRadius={[86, 74]}
            />
            <RadialBar background dataKey="percentage" cornerRadius={10} />
            <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
              <Label
                content={({ viewBox }) => {
                  if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={viewBox.cy}
                          className="fill-foreground text-4xl font-bold"
                        >
                          {chartData[1].percentage}%
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 24}
                          className="fill-muted-foreground"
                        >
                          Paid Ratio
                        </tspan>
                      </text>
                    );
                  }
                }}
              />
            </PolarRadiusAxis>
          </RadialBarChart>
        </ChartContainer>
      </CardContent>
      <CardFooter className="flex-col gap-2 text-sm">
        <div className="flex items-center gap-2 font-medium leading-none text-muted-foreground">
          {totalBayar} dari {totalLulus} peserta telah membayar
        </div>
      </CardFooter>
    </Card>
  );
}
